import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetCategoryQuery, useGetCategorySubCategoriesQuery, useUpdateCategorySubCategoriesMutation, useDeleteSubCategoryMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { showSuccess, showError } from '../../../utils/toast';

interface SubCategoryRow {
  id?: number;
  title: string;
  title_ar: string;
}

const AdminCategorySubCategories: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const categoryId = parseInt(id!);
  
  const { data: categoryData, isLoading: isLoadingCategory } = useGetCategoryQuery(categoryId);
  const { data: subCategoriesData, isLoading: isLoadingSubCategories, refetch } = useGetCategorySubCategoriesQuery(categoryId);
  const [updateSubCategories, { isLoading: isUpdating }] = useUpdateCategorySubCategoriesMutation();
  const [deleteSubCategory] = useDeleteSubCategoryMutation();

  const [subCategories, setSubCategories] = useState<SubCategoryRow[]>([]);

  useEffect(() => {
    if (subCategoriesData?.data) {
      const subs = subCategoriesData.data.map((sub: any) => ({
        id: sub.id,
        title: sub.title || '',
        title_ar: sub.title_ar || '',
      }));
      if (subs.length === 0) {
        subs.push({
          title: '',
          title_ar: '',
        });
      }
      setSubCategories(subs);
    }
  }, [subCategoriesData]);

  const handleInputChange = (index: number, field: keyof SubCategoryRow, value: string) => {
    setSubCategories(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addRow = () => {
    setSubCategories(prev => [...prev, {
      title: '',
      title_ar: '',
    }]);
  };

  const removeRow = async (index: number) => {
    const subCategory = subCategories[index];
    if (subCategory.id) {
      // Delete from backend
      const confirmed = window.confirm('Are you sure you want to delete? It may affect your products information');
      if (!confirmed) return;
      
      try {
        await deleteSubCategory(subCategory.id).unwrap();
        showSuccess('SubCategory deleted successfully!');
        refetch();
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to delete subcategory. Please try again.');
      }
    } else {
      // Just remove from UI
      setSubCategories(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const submitData = {
        title: subCategories.map(sub => sub.title),
        title_ar: subCategories.map(sub => sub.title_ar),
        sub_cat_id: subCategories.map(sub => sub.id || null),
      };

      const result = await updateSubCategories({ id: categoryId, data: submitData }).unwrap();
      showSuccess(result.message || 'SubCategories updated successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update subcategories. Please try again.');
    }
  };

  if (isLoadingCategory || isLoadingSubCategories) {
    return <PageLoader />;
  }

  const category = categoryData?.data;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {category?.name || category?.title} - SubCategories
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">Dashboard</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/categories'); }} className="text-muted text-hover-primary">Categories</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body p-5">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <button type="button" className="btn btn-base float-end btn-sm" onClick={addRow}>
                    + Add New
                  </button>
                  <br /><br />
                  
                  <div className="row my-3">
                    <div className="col-md-4"><h5>Title</h5></div>
                    <div className="col-md-4"><h5>Title Arabic <small>(optional)</small></h5></div>
                  </div>

                  {subCategories.map((subCategory, index) => (
                    <div key={index} className="row my-5">
                      <div className="col-md-4">
                        <input
                          type="text"
                          name={`title[${index}]`}
                          className="form-control"
                          placeholder="Enter Title"
                          value={subCategory.title}
                          onChange={(e) => handleInputChange(index, 'title', e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-md-4">
                        <input
                          type="text"
                          name={`title_ar[${index}]`}
                          className="form-control"
                          placeholder="Enter Title Arabic"
                          value={subCategory.title_ar}
                          onChange={(e) => handleInputChange(index, 'title_ar', e.target.value)}
                        />
                      </div>
                      <div className="col-md-2">
                        <button
                          type="button"
                          className="btn btn-base float-end btn-sm"
                          onClick={() => removeRow(index)}
                        >
                          - Remove
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="text-center mt-5">
                    <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/categories'); }} className="btn btn-light me-3">
                      Back
                    </a>
                    <button type="submit" className="btn btn-base" disabled={isUpdating}>
                      <span className="indicator-label">Update</span>
                      {isUpdating && (
                        <span className="indicator-progress">
                          Please wait...
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCategorySubCategories;

